import { Box, Typography, useMediaQuery } from '@mui/material';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as BackArrow } from 'src/assets/svg/back-arrow.svg';
import { ReactComponent as SageLogo } from 'src/assets/svg/sage-logo.svg';
import { ReactComponent as SantanderLogo } from 'src/assets/svg/santander-logo.svg';
import { Button } from 'src/components/button';
import { Section } from 'src/components/section';
import { AcknowledgeSummaryBox } from 'src/features/agreements/acknowledge-summary-box';
import { AgreementDrawer } from 'src/features/agreements/agreement-drawer';
import { theme } from 'src/styles/theme';
import { useHandleQueryError } from 'src/lib/hooks/use-handle-query-error';

import { PaymentOptionBanner } from '../../components/payment-option-banner';
import { PaymentPlanMobileOverview } from '../../features/payment-plan';
import { RoomCard } from '../../features/room-card';
import { getTheme } from '../../utils/getTheme';

import { AgreementsViewProps, AgreementType } from './types';
import { styles } from './styles';

export const AgreementsView: FC<AgreementsViewProps> = ({
    onGoBack,
    hideBackButton,
    roomData,
    hasRoommates,
    currency,
    hasDiscount,
    paymentRequired,
    loading,
    error,
    isButtonDisabled,
    onSubmit,
    terms,
    licence,
    drawer,
    agreements,
    showTermsDrawer,
    showLicenceDrawer,
    building,
    paymentPlan,
    termDuration,
    termStartDate,
    termEndDate,
    numberOfStudents,
}) => {
    const { t } = useTranslation();
    const { isHosTheme } = getTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const isBookingTermsSigned = terms.checkboxVal && !!terms.email && terms.isValid;

    const isLicenceAgreementSigned = licence.checkboxVal && !!licence.email && licence.isValid;
    const handleQueryError = useHandleQueryError();
    const isMadridOriaBuilding = building.building_slug === 'madrid-oria';

    if (error) {
        handleQueryError(error, roomData.academic_year);
    }

    return (
        <Box css={styles.layout}>
            {!isSmallScreen && !hideBackButton && (
                <Box css={styles.back} onClick={onGoBack}>
                    <BackArrow css={styles.icon} />
                    <Typography>{t('agreements_back_button_content')}</Typography>
                </Box>
            )}

            <Section header={t('agreements_title')}>
                {!isSmallScreen ? (
                    <Typography variant="body1">{t('agreements_sub_text')}</Typography>
                ) : (
                    <Typography variant="body3">{t('agreements_subheader')}</Typography>
                )}

                {isSmallScreen && (
                    <Box css={styles.wrapBox}>
                        <AcknowledgeSummaryBox
                            signed={isBookingTermsSigned}
                            content={t('agreements_summary_box_booking_terms_conditions')}
                            showAgreementDrawer={showTermsDrawer}
                        />
                        <AcknowledgeSummaryBox
                            signed={isLicenceAgreementSigned}
                            content={t('agreements_summary_box_licence_agreement')}
                            showAgreementDrawer={showLicenceDrawer}
                        />
                    </Box>
                )}

                {/* Drawer */}
                <AgreementDrawer
                    open={drawer.open}
                    closeFn={drawer.onClose}
                    currentAgreement={drawer.agreement}
                    totalAgreements={drawer.total}
                >
                    {drawer.current === AgreementType.Terms ? agreements.terms : agreements.licence}
                </AgreementDrawer>

                {/* Desktop agreements */}
                {!isSmallScreen && (
                    <Fragment>
                        {agreements.terms}
                        {agreements.licence}
                    </Fragment>
                )}
            </Section>

            <Section header={t('booking_summary_title')} style={styles.mobileSummary}>
                <Box css={styles.roomCardWrapper}>
                    <RoomCard
                        building={building}
                        roomData={roomData}
                        hasDiscount={hasDiscount}
                        isAgreementsPage
                        termDuration={termDuration}
                        termStartDate={termStartDate}
                        termEndDate={termEndDate}
                        numberOfStudents={numberOfStudents}
                    />
                </Box>

                <PaymentPlanMobileOverview
                    createdPaymentPlan={paymentPlan}
                    hasDiscount={hasDiscount}
                    showTotalPayment
                    currency={currency}
                />
            </Section>

            {paymentRequired && isSmallScreen && isHosTheme && (
                <Box css={styles.paymentOptionBannerWrapper}>
                    <PaymentOptionBanner
                        showPaymentOptions
                        showSantanderCards={isMadridOriaBuilding}
                    />
                </Box>
            )}

            <Button
                variant="contained"
                css={styles.cta}
                disabled={isButtonDisabled}
                onClick={onSubmit}
                loading={loading}
                id="booking-submit"
            >
                {paymentRequired
                    ? t('agreements_cta_payment')
                    : t('agreements_cta_complete_booking')}
            </Button>

            {error && (
                <Box css={styles.errorBox}>
                    <Typography color={theme.palette.primary.main} variant="subtitle1">
                        {error.message}
                    </Typography>
                </Box>
            )}

            <Box css={styles.logoWrapper}>
                {paymentRequired && (
                    <Box>
                        {isMadridOriaBuilding ? (
                            <SantanderLogo css={styles.santanderLogo} />
                        ) : (
                            <SageLogo />
                        )}
                        <Typography>
                            {t(
                                isMadridOriaBuilding
                                    ? 'agreements_payment_content_santander'
                                    : 'agreements_payment_content'
                            )}
                        </Typography>
                    </Box>
                )}

                {isSmallScreen && (
                    <Box css={[styles.back, styles.extraPadding]} onClick={onGoBack}>
                        {hasRoommates
                            ? t('agreements_back_to_roommates')
                            : t('agreements_back_content')}
                    </Box>
                )}
            </Box>

            {isSmallScreen && paymentRequired && !isHosTheme && (
                <PaymentOptionBanner showSantanderCards={isMadridOriaBuilding} />
            )}
        </Box>
    );
};
